import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
} from 'react-native';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Ionicons } from '@expo/vector-icons';
import { databaseService } from '../../services/databaseService';
import { APP_CONSTANTS } from '../../config/constants';
import theme from '../../config/theme';
import { FieldOrderingLogger } from '../../utils/logger';
import { assertStructuralOnline } from '../../services/checklist/ChecklistStructuralOfflineGate';
import { checklistStructuralMutationService } from '../../services/checklist/ChecklistStructuralMutationService';

interface FieldItem {
  id: string;
  grupo: string;
  nome: string;
  ordem: number;
  retired_at?: string | null;
}

interface Props {
  // opcional para filtrar por template
  templateId?: string;
}

/**
 * Componente reutilizável para exibição e reordenação de campos agrupados.
 * 1. Agrupa os campos por `grupo`.
 * 2. Permite arrastar os grupos e os campos dentro de cada grupo.
 * 3. Persiste a nova ordem pelo mutador estrutural do checklist.
 */
const FieldOrderingBoard: React.FC<Props> = ({ templateId }) => {
  const [fields, setFields] = useState<FieldItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [blockedMessage, setBlockedMessage] = useState<string | null>(null);
  // Flag para evitar múltiplos salvamentos concorrentes
  const savingRef = useRef(false);

  // Função para logar estrutura atual
  const logEstruturaAtual = (grupos: [string, FieldItem[]][]) => {
    const estrutura = grupos.map(([grupo, itens]) => ({
      grupo,
      campos: itens.map((f) => ({ nome: f.nome, ordem: f.ordem, id: f.id }))
    }));
    FieldOrderingLogger.Main.info('[ESTRUTURA] Agrupamento e ordem atual:', JSON.stringify(estrutura, null, 2));
  };

  // carregar campos do banco
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const query: any = {};
        if (templateId) query.template_id = templateId;
        const items = await databaseService.listItems<FieldItem>(
          APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CAMPOS_TEMPLATE.NAME,
          query
        );
        const activeItems = items.filter(item => !item.retired_at);
        // ordenar inicialmente pela coluna ordem
        activeItems.sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));
        if (mounted) setFields(activeItems);
      } catch (error) {
        FieldOrderingLogger.Main.error('Erro ao carregar campos:', error);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [templateId]);

  // grupos calculados (ordenados pela ordem do primeiro item)
  const groups = useMemo(() => {
    const groupMap = new Map<string, FieldItem[]>();
    fields.forEach((f) => {
      const grpName = f.grupo || 'Sem grupo';
      if (!groupMap.has(grpName)) groupMap.set(grpName, []);
      groupMap.get(grpName)!.push(f);
    });
    // ordenar cada grupo internamente
    groupMap.forEach((arr) => arr.sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0)));

    // ordenar grupos pela menor ordem encontrada dentre seus itens
    const result = Array.from(groupMap.entries()).sort((a, b) => {
      const firstA = a[1][0];
      const firstB = b[1][0];
      return (firstA.ordem ?? 0) - (firstB.ordem ?? 0);
    });
    return result; // [ [groupName, FieldItem[]] ]
  }, [fields]);

  // ids dos grupos em ordem atual
  const groupIds = useMemo(() => groups.map((g) => g[0]), [groups]);

  /* -------------------- DND Sensors -------------------- */
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  /* -------------------- Persistência -------------------- */
  const recomputeOrderAndPersist = async (updatedGroups: [string, FieldItem[]][]) => {
    const gate = assertStructuralOnline('field', 'reorder');
    if (!gate.allowed) {
      setBlockedMessage(gate.message);
      return;
    }
    if (!templateId) {
      setBlockedMessage('Selecione um modelo de checklist antes de reordenar os campos.');
      return;
    }

    setBlockedMessage(null);
    // Impedir reentrância caso ainda esteja persistindo
    if (savingRef.current) {
      FieldOrderingLogger.Main.warn('[SKIP] Persistência ainda em andamento, chamada ignorada');
      return;
    }
    savingRef.current = true;

    // ----- LOG PRE ----- (apenas id, grupo, nome, ordem)
    FieldOrderingLogger.Main.info('[ORDER-PRE] Estrutura recebida para cálculo:', JSON.stringify(updatedGroups.map(([g, its]) => ({ grupo: g, itens: its.map(i => ({ id: i.id, grupo: g, nome: i.nome, ordem: i.ordem })) })), null, 2));

    // 1) Construir novo orderMap sequencial começando em 1
    const orderMap: Record<string, number> = {};
    let current = 1;
    updatedGroups.forEach(([_, items]) => {
      items.forEach((it) => {
        orderMap[it.id] = current++;
      });
    });

    FieldOrderingLogger.Main.info('[NOVA_ORDERMAP]', JSON.stringify(orderMap, null, 2));

    // ----- Atualização otimista do estado local -----
    setFields((prev) => prev.map((f) => ({ ...f, ordem: orderMap[f.id] })));
    FieldOrderingLogger.Main.info('[ESTADO-OTIMISTA] Fields atualizados localmente com novo orderMap');

    // Log de pending ops ANTES de persistir
    try {
      const pending = await databaseService.listItems<any>('pending_operations');
      FieldOrderingLogger.Main.info(`[PENDING_OPS:PRE] total ${pending.length}:`, JSON.stringify(pending.map((p: any) => ({ id: p.id, op: p.operation, item: p.item_id })), null, 2));
    } catch (e) {
      FieldOrderingLogger.Main.error('Erro ao listar pending_operations (pre):', e);
    }

    // 2) Atualizar banco (apenas quando ordem mudou)
    const updates: Array<{ id: string; updates: Record<string, unknown> }> = [];
    updatedGroups.forEach(([_, items]) => {
      items.forEach((it) => {
        const newOrder = orderMap[it.id];
        if (it.ordem !== newOrder) {
          updates.push({ id: it.id, updates: { ordem: newOrder } });
        }
      });
    });

    try {
      await checklistStructuralMutationService.reorderFields(templateId, updates);
      FieldOrderingLogger.Main.info(`[DB] ${updates.length} registros atualizados`);
    } catch (e) {
      FieldOrderingLogger.Main.error('Erro ao persistir ordens:', e);
    }

    // 3) Recarregar lista do DB para garantir consistência
    try {
      const query: any = {};
      if (templateId) query.template_id = templateId;
      const fresh = await databaseService.listItems<FieldItem>(
        APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CAMPOS_TEMPLATE.NAME,
        query
      );
      const activeFresh = fresh.filter(item => !item.retired_at);
      activeFresh.sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));
      setFields(activeFresh);
      // ----- LOG POST ----- (apenas id, grupo, nome, ordem)
      const gruposFresh: [string, FieldItem[]][] = (() => {
        const m = new Map<string, FieldItem[]>();
        activeFresh.forEach((f) => {
          const g = f.grupo || 'Sem grupo';
          if (!m.has(g)) m.set(g, []);
          m.get(g)!.push(f);
        });
        return Array.from(m.entries());
      })();
      FieldOrderingLogger.Main.info('[ORDER-POST] Estrutura depois de persistir:', JSON.stringify(gruposFresh.map(([g, its]) => ({ grupo: g, itens: its.map(i => ({ id: i.id, grupo: g, nome: i.nome, ordem: i.ordem })) })), null, 2));
    } catch (e) {
      FieldOrderingLogger.Main.error('Erro ao recarregar campos após persistir:', e);
    }

    // Log de pending ops DEPOIS de persistir
    try {
      const pending = await databaseService.listItems<any>('pending_operations');
      FieldOrderingLogger.Main.info(`[PENDING_OPS:POST] total ${pending.length}:`, JSON.stringify(pending.map((p: any) => ({ id: p.id, op: p.operation, item: p.item_id })), null, 2));
    } catch (e) {
      FieldOrderingLogger.Main.error('Erro ao listar pending_operations (post):', e);
    } finally {
      savingRef.current = false; // liberar novos drags
    }
  };

  /* -------------------- Eventos de drag -------------------- */
  const handleGroupDragEnd = async (event: any) => {
    if (savingRef.current) return; // Bloquear enquanto salva
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = groupIds.indexOf(active.id as string);
    const newIndex = groupIds.indexOf(over.id as string);

    if (oldIndex === -1 || newIndex === -1) return;

    const reorderedGroups = arrayMove(groups, oldIndex, newIndex);
    await recomputeOrderAndPersist(reorderedGroups);
  };

  const handleFieldDragEnd = async (
    groupName: string,
    event: any
  ) => {
    if (savingRef.current) return; // Bloquear enquanto salva
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const groupPair = groups.find((g) => g[0] === groupName);
    if (!groupPair) return;

    const [gName, items] = groupPair;
    const ids = items.map((i) => i.id);

    const oldIndex = ids.indexOf(active.id as string);
    const newIndex = ids.indexOf(over.id as string);

    if (oldIndex === -1 || newIndex === -1) return;

    const newItems = arrayMove(items, oldIndex, newIndex);
    const newGroups = groups.map((g) => (g[0] === gName ? [gName, newItems] : g)) as [
      string,
      FieldItem[]
    ][];

    await recomputeOrderAndPersist(newGroups);
  };

  /* -------------------- Render -------------------- */
  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary.default} />
      </View>
    );
  }

  return (
    <View style={styles.groupsContainer}>
      {blockedMessage ? (
        <View style={styles.blockedBanner}>
          <Ionicons name="cloud-offline-outline" size={18} color={theme.colors.util.error} />
          <Text style={styles.blockedBannerText}>{blockedMessage}</Text>
        </View>
      ) : null}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleGroupDragEnd}>
        <SortableContext items={groupIds} strategy={verticalListSortingStrategy}>
          {groups.map(([grpName, items]) => (
            <GroupSortable key={grpName} id={grpName} title={grpName || 'Sem grupo'}>
              {/* Contexto de DnD interno para campos */}
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={(ev: any) => handleFieldDragEnd(grpName, ev)}
              >
                <SortableContext items={items.map((it) => it.id)} strategy={verticalListSortingStrategy}>
                  {items.map((field) => (
                    <FieldSortable key={field.id} id={field.id} title={field.nome} ordem={field.ordem} />
                  ))}
                </SortableContext>
              </DndContext>
            </GroupSortable>
          ))}
        </SortableContext>
      </DndContext>
    </View>
  );
};

/* -------------------- Componentes Sortable -------------------- */
interface GroupProps {
  id: string;
  title: string;
  children: React.ReactNode;
}

const GroupSortable: React.FC<GroupProps> = ({ id, title, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  } as any;

  return (
    <View ref={setNodeRef} style={[styles.groupContainer, style]}>
      <View style={styles.groupHeader}>
        <Ionicons name="reorder-three" size={20} color={theme.colors.text.secondary} {...listeners} />
        <Text style={styles.groupTitle}>{title}</Text>
      </View>
      {children}
    </View>
  );
};

interface FieldProps {
  id: string;
  title: string;
  ordem: number;
}

const FieldSortable: React.FC<FieldProps> = ({ id, title, ordem }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  } as any;

  return (
    <View ref={setNodeRef} style={[styles.fieldRow, style]}>{/* linha */}
      <Ionicons
        name="reorder-two"
        size={18}
        color={theme.colors.text.secondary}
        style={styles.fieldDragHandle}
        {...listeners}
        {...attributes}
      />
      <Text style={styles.fieldName}>{title} <Text style={styles.ordemBadge}>#{ordem}</Text></Text>
    </View>
  );
};

/* -------------------- Styles -------------------- */
const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background.secondary,
  },
  groupsContainer: {
    marginTop: theme.spacing.md,
  },
  blockedBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginHorizontal: theme.spacing.md,
    marginBottom: theme.spacing.md,
    padding: theme.spacing.sm,
    backgroundColor: theme.colors.background.primary,
    borderLeftWidth: 3,
    borderLeftColor: theme.colors.util.error,
  },
  blockedBannerText: {
    flex: 1,
    marginLeft: theme.spacing.sm,
    color: theme.colors.text.secondary,
    fontSize: theme.fontSizes.sm,
  },
  groupContainer: {
    backgroundColor: theme.colors.background.primary,
    marginHorizontal: theme.spacing.md,
    marginBottom: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    ...theme.shadows.sm,
  },
  groupHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    backgroundColor: theme.colors.background.tertiary || theme.colors.background.secondary,
    borderTopLeftRadius: theme.borderRadius.md,
    borderTopRightRadius: theme.borderRadius.md,
  },
  groupTitle: {
    marginLeft: theme.spacing.sm,
    fontSize: theme.fontSizes.lg,
    fontWeight: theme.fontWeights.semibold as any,
    color: theme.colors.text.primary,
  },
  fieldRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    borderTopWidth: 1,
    borderTopColor: theme.colors.border.light,
  },
  fieldDragHandle: {
    marginRight: theme.spacing.sm,
  },
  fieldName: {
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.secondary,
  },
  ordemBadge: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.util.info,
    marginLeft: 4,
  },
});

export default FieldOrderingBoard;
